import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flexRender } from '@tanstack/react-table';
import { DragHandleDots2Icon } from '@radix-ui/react-icons';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { BaseBlock } from '../../schedule/components/styled-strips';
import { EditableText } from '../../../components/editable-text/EditableText';
import { AddStripMenu } from '../../schedule/components/add-strip-menu';
import { getModifier } from '../../../core/services/event.service';
import { useSelection } from '../../../core/contexts/selection.context';
import Styles from '../stripboard.module.scss';

import type { StripProps } from './stripboard-table';

type BannerStripProps = StripProps & {
  onUpdateText: (text: string) => void;
  projectId: string;
};

export const BannerStripComponent: React.FC<BannerStripProps> = observer(
  ({ row, dayId, onUpdateText, projectId }) => {
    const strip = row.original;

    const { elements, select } = useSelection();

    const { attributes, listeners, setNodeRef, transform, transition, isDragging, isSorting } =
      useSortable({
        id: strip?._id,
        data: {
          dayId,
          type: 'row',
          row: strip,
        },
      });

    const isSelected = elements?.includes(strip._id);

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const modifier = getModifier(e.nativeEvent);

      select?.(strip._id, modifier);
    };

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isSorting && isSelected ? 0.5 : 1,
    };

    if (!strip) return null;

    const cells = row.getVisibleCells();

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${Styles.bannerStrip} ${isSelected ? Styles.selected : ''}`}
        onClick={handleClick}
        data-dragged={isDragging}
      >
        <div
          className={Styles.stickyBanner}
          style={{
            width: 0,
          }}
        >
          <div className={Styles.stickyBannerContent}>
            <BaseBlock>
              <BlockEditableText onSubmit={onUpdateText} defaultText={'Setup'} maxLength={60}>
                <BlockName>{strip.data.text}</BlockName>
              </BlockEditableText>
            </BaseBlock>
          </div>
        </div>

        <div className={Styles.dragHandle} {...attributes} {...listeners}>
          <DragHandleDots2Icon color="var(--color-surfaces-bg-elevation-5)" />
        </div>

        {cells.map((cell) => (
          <div
            key={cell.id}
            className={Styles.cell}
            style={{
              width: cell.column.getSize(),
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        ))}

        <div className={Styles.actionButton}>
          <AddStripMenu
            stripId={strip._id}
            projectId={projectId}
            position={strip.position}
            stripType={strip.type}
            isDark
          />
        </div>
      </div>
    );
  },
);

export const BannerStrip = React.memo(BannerStripComponent);

const BlockEditableText = styled(EditableText)`
  width: 100%;
`;

const BlockName = styled.p`
  width: 100%;
  height: 3.2rem;
  padding: 0.8rem;
  text-align: center;
`;
